/**
 * AC-ADM-3 + Persona Groups: Persona Management (REAL)
 *
 * Master–detail screen: a 240px rail (All personas / Groups / Ungrouped / +
 * New group) beside a detail pane. A group holds module permissions every
 * member persona inherits (floor semantics — see personaGroupLogic.ts). The
 * heavy lifting (module matrix + the three pill states, the five group
 * flows, the pure inheritance/impact math) lives in sibling files:
 *   - ../components/ModuleMatrix.tsx        — the permission matrix
 *   - ../components/PersonaRow.tsx           — one persona's expandable card
 *   - ../components/PersonaGroupPane.tsx     — the group detail pane
 *   - ../components/PersonaGroupDialogs.tsx  — New/from-selection/Move/Add/Delete
 *   - ../lib/personaGroupLogic.ts            — pure inheritance/impact math
 *
 * GraphQL operations:
 *   Query:    AcListPersonas, AcListPersonaGroups, AcPersonaModuleOptions
 *   Mutations: AcCreatePersona, AcUpsertPersonaModule, AcDeactivatePersonaModule,
 *              AcCreatePersonaGroup, AcUpsertPersonaGroupModule,
 *              AcDeactivatePersonaGroupModule, AcAssignPersonaToGroup,
 *              AcDeletePersonaGroup
 *
 * Schema-miss behavior: if listPersonas is not yet on the schema, the page
 * displays an amber note and "No personas found" (no currentUser fallback).
 * If listPersonaGroups isn't yet on the schema, groups degrade to an empty
 * list — the page shows the "no groups yet" teaching empty state, which is
 * true either way (no groups exist, or the feature isn't live yet).
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { Folder, Layers, Plus, Users } from "lucide-react";

import { AdminWall, isAuthWallError } from "../components/AdminWall";
import SearchableSelect, { type SelectOption } from "../components/SearchableSelect";
import { ActionToggles } from "../components/ModuleMatrix";
import PersonaRow from "../components/PersonaRow";
import PersonaGroupPane from "../components/PersonaGroupPane";
import {
  NewGroupDialog,
  GroupFromSelectionDialog,
  MoveDialog,
  AddMemberDialog,
  DeleteGroupDialog,
} from "../components/PersonaGroupDialogs";
import { isModuleId, type ModuleId, catalogActions, FEATURE_CATALOG } from "../../config/featureCatalog";
import {
  LIST_PERSONAS_QUERY,
  LIST_PERSONA_GROUPS_QUERY,
  LIST_USERS_QUERY,
  PERSONA_MODULE_OPTIONS_QUERY,
  CREATE_PERSONA_MUTATION,
  LIST_AI_TEMPLATES_QUERY,
  PERSONA_TEMPLATES_QUERY,
  type ListPersonasResult,
  type ListPersonaGroupsResult,
  type ListUsersResult,
  type PersonaModuleOptionsResult,
  type AcAdminPersona,
  type AcPersonaGroup,
  type CreatePersonaVars,
  type CreatePersonaResult,
  type ListAiTemplatesResult,
  type PersonaTemplatesResult,
  type PersonaTemplate,
} from "../graphql/adminOps";
import type { UserCountState } from "../lib/personaGroupLogic";

// Re-exported for backward compatibility — PersonasPage.defaultTab.test.ts
// imports these from "./PersonasPage"; the canonical definitions now live in
// personaGroupLogic.ts (shared with ModuleMatrix.tsx and PersonaRow.tsx, so
// group and persona scopes can't drift apart on sentinel/exclusivity rules).
export {
  DEFAULT_TAB_ACTION,
  computeDefaultTabUpserts,
  isDefaultTabSettable,
  resolveDefaultTabRowState,
} from "../lib/personaGroupLogic";
export type { DefaultTabRowState } from "../lib/personaGroupLogic";

// ---------------------------------------------------------------------------
// Create Persona Modal — unchanged behaviour, now importing the shared
// ActionToggles from ModuleMatrix.tsx instead of a page-local copy.
// ---------------------------------------------------------------------------
interface DraftModule {
  moduleId: string;
  actions: string[];
}

function CreatePersonaModal({
  moduleIdOptions,
  existingCodes,
  onClose,
  onCreated,
}: {
  moduleIdOptions: SelectOption[];
  existingCodes: string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [modules, setModules] = useState<DraftModule[]>([]);
  const [addModuleId, setAddModuleId] = useState<string | null>(null);

  const [createPersona, { loading, error }] = useMutation<CreatePersonaResult, CreatePersonaVars>(CREATE_PERSONA_MUTATION, {
    onCompleted: () => { onCreated(); onClose(); },
  });

  const trimmedCode = code.trim();
  const trimmedName = displayName.trim();
  const dupCode = trimmedCode.length > 0 && existingCodes.some((c) => c === trimmedCode);
  const canSubmit = trimmedCode.length > 0 && trimmedName.length > 0 && !dupCode && !loading;

  const availableToAdd = useMemo(
    () => moduleIdOptions.filter((o) => !modules.some((m) => m.moduleId === o.value)),
    [moduleIdOptions, modules],
  );

  const addModule = () => {
    if (!addModuleId || modules.some((m) => m.moduleId === addModuleId)) return;
    const seeded = isModuleId(addModuleId) ? [...catalogActions(addModuleId)] : [];
    setModules((prev) => [...prev, { moduleId: addModuleId, actions: seeded }]);
    setAddModuleId(null);
  };

  const submit = () => {
    if (!canSubmit) return;
    void createPersona({
      variables: {
        code: trimmedCode,
        displayName: trimmedName,
        modules: modules.length
          ? modules.map((m, i) => ({ moduleId: m.moduleId, sortOrder: i, actions: m.actions }))
          : undefined,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Create persona">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">New Persona</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
            ×
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="persona-code" className="block text-sm font-medium mb-1">
                Code <span className="text-destructive">*</span>
              </label>
              <input
                id="persona-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. CARE_MANAGER"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
              />
              {dupCode && <p className="text-xs text-destructive mt-1">A persona with this code already exists.</p>}
            </div>
            <div>
              <label htmlFor="persona-name" className="block text-sm font-medium mb-1">
                Display name <span className="text-destructive">*</span>
              </label>
              <input
                id="persona-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Care Manager"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <fieldset className="rounded-lg border border-border p-3">
            <legend className="text-sm font-medium px-1">
              Modules <span className="text-xs text-muted-foreground font-normal">(optional — toggle read/write per action)</span>
            </legend>
            {modules.length === 0 ? (
              <p className="text-sm text-muted-foreground italic mb-3">No modules yet. Add one below, or create the persona and configure modules afterward.</p>
            ) : (
              <div className="space-y-2 mb-3">
                {modules.map((m) => (
                  <div key={m.moduleId} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
                    <span className="font-mono text-sm font-semibold flex-1">{m.moduleId}</span>
                    <ActionToggles
                      moduleId={m.moduleId}
                      actions={m.actions}
                      onChange={(next) => setModules((prev) => prev.map((x) => (x.moduleId === m.moduleId ? { ...x, actions: next } : x)))}
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${m.moduleId}`}
                      title="Remove module"
                      onClick={() => setModules((prev) => prev.filter((x) => x.moduleId !== m.moduleId))}
                      className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium mb-1 text-muted-foreground">Add module</label>
                <SearchableSelect value={addModuleId} onChange={setAddModuleId} options={availableToAdd} ariaLabel="Module to add" placeholder="Select a module…" />
              </div>
              <button
                type="button"
                onClick={addModule}
                disabled={!addModuleId}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-border hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
          </fieldset>

          {error && <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error.message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={!canSubmit} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors">
              {loading ? "Creating..." : "Create Persona"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// usePersonaUserCounts — "N users hold this persona", derived from the
// existing listUsers(personaCode) filter + pageInformation.totalRecords (no
// new backend field needed). Fetched once per persona-code set and cached
// locally; re-fetched when the code set changes (e.g. a persona is created).
//
// Tri-state (number | "loading" | "failed") rather than number | undefined —
// a destructive dialog (delete group) reads these counts to state its
// consequences, and collapsing "not resolved yet" into a bare `undefined`
// invited call sites to `?? 0`, silently asserting a confident zero for a
// count that was never actually measured. Newly-seen codes are marked
// "loading" the instant they're requested so no consumer ever sees a gap.
// ---------------------------------------------------------------------------
function usePersonaUserCounts(personaCodes: string[]) {
  const client = useApolloClient();
  const [counts, setCounts] = useState<Record<string, UserCountState>>({});
  const key = personaCodes.slice().sort().join(",");

  useEffect(() => {
    if (personaCodes.length === 0) return;
    let cancelled = false;
    setCounts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const code of personaCodes) {
        if (!(code in next)) {
          next[code] = "loading";
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    void Promise.all(
      personaCodes.map((code) =>
        client
          .query<ListUsersResult>({
            query: LIST_USERS_QUERY,
            variables: { input: { personaCode: code, pageSize: 1 } },
            fetchPolicy: "cache-first",
          })
          .then((res) => [code, res.data.listUsers.pageInformation?.totalRecords ?? 0] as const)
          .catch(() => [code, "failed" as const] as const),
      ),
    ).then((entries) => {
      if (cancelled) return;
      setCounts((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return counts;
}

// ---------------------------------------------------------------------------
// Rail selection
// ---------------------------------------------------------------------------
type RailSelection = { type: "all" } | { type: "ungrouped" } | { type: "group"; code: string };

function railKey(sel: RailSelection): string {
  return sel.type === "group" ? `group:${sel.code}` : sel.type;
}

// ---- Main Page ----
export default function PersonasPage() {
  const {
    data: listData,
    previousData: listPrevData,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useQuery<ListPersonasResult>(LIST_PERSONAS_QUERY, { fetchPolicy: "cache-first", errorPolicy: "all" });

  const {
    data: groupsData,
    previousData: groupsPrevData,
    refetch: refetchGroups,
  } = useQuery<ListPersonaGroupsResult>(LIST_PERSONA_GROUPS_QUERY, { fetchPolicy: "cache-first", errorPolicy: "all" });

  const { data: optionsData } = useQuery<PersonaModuleOptionsResult>(PERSONA_MODULE_OPTIONS_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  const { data: templatesData } = useQuery<ListAiTemplatesResult>(LIST_AI_TEMPLATES_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });
  const { data: personaTemplatesData, refetch: refetchPersonaTemplates } = useQuery<PersonaTemplatesResult>(
    PERSONA_TEMPLATES_QUERY,
    { fetchPolicy: "cache-first", errorPolicy: "all" },
  );

  const templateOptions: SelectOption[] = useMemo(
    () => (templatesData?.aiTemplates ?? []).filter((t) => t.isActive).map((t) => ({ value: t.id, label: `${t.name} (${t.key})` })),
    [templatesData],
  );
  const personaTemplatesByCode = useMemo(() => {
    const map: Record<string, PersonaTemplate[]> = {};
    for (const pt of personaTemplatesData?.personaTemplates ?? []) (map[pt.personaCode] ??= []).push(pt);
    return map;
  }, [personaTemplatesData]);

  const moduleIdOptions: SelectOption[] = useMemo(() => {
    const ids = new Set(optionsData?.personaModuleOptions.moduleIds ?? []);
    ids.add("HomeDemographics");
    return Array.from(ids).map((m) => ({ value: m, label: isModuleId(m) ? FEATURE_CATALOG[m as ModuleId].label : m }));
  }, [optionsData]);
  const actionCatalog: string[] = useMemo(() => optionsData?.personaModuleOptions.actions ?? [], [optionsData]);
  const lobOptions: SelectOption[] = useMemo(
    () => (optionsData?.personaModuleOptions.lineOfBusiness ?? []).map((l) => ({ value: l, label: l })),
    [optionsData],
  );

  const effectiveListData = listData ?? listPrevData;
  const listFailed = !!listError && !effectiveListData?.listPersonas;
  const personas: AcAdminPersona[] = effectiveListData?.listPersonas ?? [];
  const existingCodes = useMemo(() => personas.map((p) => p.code), [personas]);
  const groups: AcPersonaGroup[] = (groupsData ?? groupsPrevData)?.listPersonaGroups ?? [];

  const groupByCode = useMemo(() => new Map(groups.map((g) => [g.code, g] as const)), [groups]);
  const membersOf = (code: string) => personas.filter((p) => p.groupCode === code);
  const ungrouped = personas.filter((p) => !p.groupCode);

  const userCounts = usePersonaUserCounts(existingCodes);

  const onRefreshAll = () => {
    void refetchList();
    void refetchGroups();
    void refetchPersonaTemplates();
  };

  const [sel, setSel] = useState<RailSelection>({ type: "all" });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [creatingPersona, setCreatingPersona] = useState(false);
  const [dialog, setDialog] = useState<
    | { kind: "new-group" }
    | { kind: "group-from-selection" }
    | { kind: "move"; personas: AcAdminPersona[] }
    | { kind: "add-member"; groupCode: string }
    | { kind: "delete-group"; groupCode: string }
    | null
  >(null);

  const togglePick = (code: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const railOptions = useMemo(() => {
    const opts: SelectOption[] = [{ value: "all", label: `All personas (${personas.length})` }];
    for (const g of groups) opts.push({ value: `group:${g.code}`, label: `${g.displayName} (${membersOf(g.code).length})` });
    opts.push({ value: "ungrouped", label: `Ungrouped (${ungrouped.length})` });
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personas, groups]);

  const selectedRailValue = railKey(sel);

  const selectionBar = picked.size > 0 && (
    <div className="sticky bottom-0 z-20 flex items-center gap-3 flex-wrap px-3.5 py-2.5 rounded-xl bg-card border border-primary/35 shadow-lg mt-2">
      <span className="font-semibold text-sm tabular-nums">{picked.size} selected</span>
      <span className="flex-1" />
      <button type="button" onClick={() => setPicked(new Set())} className="px-2.5 py-1 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors">
        Clear
      </button>
      <button
        type="button"
        onClick={() => setDialog({ kind: "move", personas: personas.filter((p) => picked.has(p.code)) })}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
      >
        Move to group
      </button>
      <button
        type="button"
        onClick={() => setDialog({ kind: "group-from-selection" })}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
      >
        <Layers className="w-3.5 h-3.5" aria-hidden="true" />
        Create group from selection
      </button>
    </div>
  );

  const renderPane = () => {
    if (listLoading && personas.length === 0) {
      return (
        <div className="flex flex-col gap-3">
          <div className="h-6 w-44 rounded-lg bg-muted animate-pulse" />
          <div className="h-3.5 w-80 rounded bg-muted animate-pulse" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[58px] rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      );
    }

    if (sel.type === "all") {
      return (
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold">Personas</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                All <b className="font-semibold text-foreground tabular-nums">{personas.length}</b> personas across{" "}
                <b className="font-semibold text-foreground tabular-nums">{groups.length}</b> group{groups.length === 1 ? "" : "s"}. Select several to
                lift their shared permissions into a new group.
              </p>
              {listFailed && !isAuthWallError(listError) && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                  <strong>Note:</strong> listPersonas query not yet on schema. The backend needs to expose a{" "}
                  <code>listPersonas</code> query.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setCreatingPersona(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors shrink-0"
            >
              <Plus className="w-4 h-4" />
              New Persona
            </button>
          </div>

          {groups.length === 0 && personas.length > 0 && (
            <NoGroupsYetTeaser onCreateGroup={() => setDialog({ kind: "new-group" })} />
          )}

          {personas.length === 0 ? (
            <p className="text-muted-foreground text-sm">No personas found.</p>
          ) : (
            <div>
              {personas.map((p) => (
                <PersonaRow
                  key={p.code}
                  persona={p}
                  group={p.groupCode ? (groupByCode.get(p.groupCode) ?? null) : null}
                  moduleIdOptions={moduleIdOptions}
                  actionCatalog={actionCatalog}
                  lobOptions={lobOptions}
                  templateOptions={templateOptions}
                  personaTemplates={personaTemplatesByCode[p.code] ?? []}
                  userCount={userCounts[p.code]}
                  selectable
                  picked={picked.has(p.code)}
                  onTogglePick={() => togglePick(p.code)}
                  onRequestMove={() => setDialog({ kind: "move", personas: [p] })}
                  onRefresh={onRefreshAll}
                />
              ))}
            </div>
          )}
          {selectionBar}
        </div>
      );
    }

    if (sel.type === "ungrouped") {
      return (
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-xl font-bold">Ungrouped</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              These <b className="font-semibold text-foreground tabular-nums">{ungrouped.length}</b> personas manage their own
              permissions. Nothing is inherited.
            </p>
          </div>
          {ungrouped.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center flex flex-col items-center gap-1">
              <h3 className="text-sm font-semibold">Every persona belongs to a group</h3>
              <p className="text-xs text-muted-foreground">Nothing to show here. That&rsquo;s fine — grouping is optional.</p>
            </div>
          ) : (
            <div>
              {ungrouped.map((p) => (
                <PersonaRow
                  key={p.code}
                  persona={p}
                  group={null}
                  moduleIdOptions={moduleIdOptions}
                  actionCatalog={actionCatalog}
                  lobOptions={lobOptions}
                  templateOptions={templateOptions}
                  personaTemplates={personaTemplatesByCode[p.code] ?? []}
                  userCount={userCounts[p.code]}
                  showGroupChip={false}
                  selectable
                  picked={picked.has(p.code)}
                  onTogglePick={() => togglePick(p.code)}
                  onRequestMove={() => setDialog({ kind: "move", personas: [p] })}
                  onRefresh={onRefreshAll}
                />
              ))}
            </div>
          )}
          {selectionBar}
        </div>
      );
    }

    // sel.type === "group"
    const group = groupByCode.get(sel.code);
    if (!group) {
      return (
        <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center flex flex-col items-center gap-2">
          <h3 className="text-sm font-semibold">This group was deleted</h3>
          <p className="text-xs text-muted-foreground">It may have been removed in another session.</p>
          <button type="button" onClick={() => setSel({ type: "all" })} className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors mt-1">
            Back to all personas
          </button>
        </div>
      );
    }
    return (
      <PersonaGroupPane
        group={group}
        members={membersOf(group.code)}
        userCountByPersonaCode={userCounts}
        moduleIdOptions={moduleIdOptions}
        actionCatalog={actionCatalog}
        lobOptions={lobOptions}
        templateOptions={templateOptions}
        personaTemplatesByCode={personaTemplatesByCode}
        onRefresh={onRefreshAll}
        onDeleteGroup={() => setDialog({ kind: "delete-group", groupCode: group.code })}
        onAddMember={() => setDialog({ kind: "add-member", groupCode: group.code })}
        onRequestMovePersona={(p) => setDialog({ kind: "move", personas: [p] })}
      />
    );
  };

  return (
    <div>
      <AdminWall error={listError} screenLabel="Personas">
        {/* Mobile rail — collapses to a picker above the pane below `lg`. */}
        <div className="lg:hidden mb-5">
          <label htmlFor="persona-rail-select" className="block text-xs font-semibold mb-1 text-muted-foreground">
            Group
          </label>
          <SearchableSelect
            value={selectedRailValue}
            onChange={(v) => {
              if (!v) return;
              setPicked(new Set());
              if (v.startsWith("group:")) {
                setSel({ type: "group", code: v.slice(6) });
                return;
              }
              setSel({ type: v as "all" | "ungrouped" });
            }}
            options={railOptions}
            ariaLabel="Group"
          />
        </div>

        <div className="flex gap-6 items-start">
          {/* Rail */}
          <div className="hidden lg:flex w-60 shrink-0 flex-col gap-0.5 sticky top-0" role="listbox" aria-label="Persona groups">
            <RailItem
              icon={Users}
              label="All personas"
              count={personas.length}
              selected={sel.type === "all"}
              onSelect={() => { setSel({ type: "all" }); setPicked(new Set()); }}
            />
            <div className="h-px bg-border my-2.5 mx-3" />
            <div className="text-[11px] font-semibold tracking-wide text-muted-foreground px-3 pt-3.5 pb-1">Groups</div>
            {groups.length === 0 ? (
              <p className="text-xs text-muted-foreground px-3 pb-2">None yet.</p>
            ) : (
              groups.map((g) => (
                <RailItem
                  key={g.code}
                  icon={Layers}
                  label={g.displayName}
                  count={membersOf(g.code).length}
                  selected={sel.type === "group" && sel.code === g.code}
                  onSelect={() => { setSel({ type: "group", code: g.code }); setPicked(new Set()); }}
                />
              ))
            )}
            <div className="h-1.5" />
            <RailItem
              icon={Folder}
              label="Ungrouped"
              count={ungrouped.length}
              selected={sel.type === "ungrouped"}
              onSelect={() => { setSel({ type: "ungrouped" }); setPicked(new Set()); }}
            />
            <div className="h-px bg-border my-2.5 mx-3" />
            <button
              type="button"
              onClick={() => setDialog({ kind: "new-group" })}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-dashed border-border text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 hover:bg-primary/[0.06] transition-colors"
            >
              <Plus className="w-[15px] h-[15px]" aria-hidden="true" />
              New group
            </button>
          </div>

          {/* Pane */}
          <div className="flex-1 min-w-0">{renderPane()}</div>
        </div>
      </AdminWall>

      {creatingPersona && (
        <CreatePersonaModal
          moduleIdOptions={moduleIdOptions}
          existingCodes={existingCodes}
          onClose={() => setCreatingPersona(false)}
          onCreated={() => void refetchList()}
        />
      )}

      {dialog?.kind === "new-group" && (
        <NewGroupDialog
          existingGroups={groups}
          onClose={() => setDialog(null)}
          onCreated={(code) => { onRefreshAll(); setSel({ type: "group", code }); }}
        />
      )}
      {dialog?.kind === "group-from-selection" && (
        <GroupFromSelectionDialog
          selected={personas.filter((p) => picked.has(p.code))}
          existingGroups={groups}
          onClose={() => setDialog(null)}
          onCreated={(code) => { setPicked(new Set()); onRefreshAll(); setSel({ type: "group", code }); }}
        />
      )}
      {dialog?.kind === "move" && (
        <MoveDialog
          personas={dialog.personas}
          groups={groups}
          onClose={() => setDialog(null)}
          onMoved={() => { setPicked(new Set()); onRefreshAll(); }}
        />
      )}
      {dialog?.kind === "add-member" && groupByCode.get(dialog.groupCode) && (
        <AddMemberDialog
          group={groupByCode.get(dialog.groupCode)!}
          candidates={ungrouped}
          onClose={() => setDialog(null)}
          onAdded={onRefreshAll}
        />
      )}
      {dialog?.kind === "delete-group" && groupByCode.get(dialog.groupCode) && (
        <DeleteGroupDialog
          group={groupByCode.get(dialog.groupCode)!}
          members={membersOf(dialog.groupCode).map((p) => ({ code: p.code, displayName: p.displayName, userCount: userCounts[p.code] ?? "loading" }))}
          onClose={() => setDialog(null)}
          onDeleted={(outcome) => {
            onRefreshAll();
            setSel(outcome === "UNGROUP" ? { type: "ungrouped" } : { type: "all" });
          }}
        />
      )}
    </div>
  );
}

function NoGroupsYetTeaser({ onCreateGroup }: { onCreateGroup: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card px-6 py-8 text-center flex flex-col items-center gap-2">
      <Layers className="w-6 h-6 text-primary" aria-hidden="true" />
      <h3 className="text-sm font-semibold">Groups let several personas share one set of permissions</h3>
      <p className="text-xs text-muted-foreground max-w-[56ch]">
        Add a permission to a group and every persona in it receives it — you edit one place instead of five. Members
        can still add extras of their own; they just can&rsquo;t drop what the group grants.
      </p>
      <div className="flex gap-2 flex-wrap justify-center mt-2">
        <button
          type="button"
          onClick={onCreateGroup}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          Create a group
        </button>
      </div>
    </div>
  );
}

function RailItem({
  icon: Icon,
  label,
  count,
  selected,
  onSelect,
}: {
  icon: typeof Users;
  label: string;
  count: number;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={
        "flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors " +
        (selected ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent")
      }
    >
      <Icon className="w-[15px] h-[15px] shrink-0" aria-hidden="true" />
      <span className="flex-1 truncate">{label}</span>
      <span className={"text-xs tabular-nums " + (selected ? "text-primary/75" : "text-muted-foreground")}>{count}</span>
    </button>
  );
}
